import { MouseEvent, useState } from 'react';
import clsx from 'clsx';

import { useVideoModalState } from 'features/video-modal';
import {
  setIsLiteracyInsightModalVisible,
  setLiteracyInsightModalInfo,
} from 'features/literacy-insight-modal';
import { PlyrVideoUtils } from 'shared/lib/plyr-video-utils';
import { CardCornerLabel } from 'shared/ui/icons';
import { useSubjectsFromProductMap } from 'shared/lib/utils';
import { useAppDispatch } from 'shared/lib/redux';
import { OvalButton } from 'shared/ui/buttons';
import { Asset } from 'shared/api/retail';
import { eventService, XApiEventKeys } from 'shared/api/event-service';
import {
  BOOST_EM_INSIGHT_TYPE,
  BoostEmInsightDynamicContent,
  Content,
} from 'shared/api/insights';

import { getRecommendationEventContextExtension } from '../../lib';
import { LiteracyInsightHeading } from '../literacy-insight-heading';
import { isRecommendationNew } from '../../model/recommendations.service';

import { RecommendationsItemDomains } from './recommendations-item-domains';
import styles from './styles.module.scss';

export type RecommendationsItemProps = {
  id: string;
  insightType: string;
  thumbnailFallbackColor: string;
  programAssetId: string;
  createdDate: number;
  content: Content;
  dynamicContent?: BoostEmInsightDynamicContent;
  userPrograms?: Asset[];
  startRelevantDate?: number;
  height?: number | string;
  resetMaxRecommendationCardHeight?: () => void;
};

export const RecommendationsItem = ({
  id,
  thumbnailFallbackColor,
  programAssetId,
  userPrograms,
  startRelevantDate,
  height,
  resetMaxRecommendationCardHeight,
  content,
  dynamicContent,
  insightType,
  createdDate,
}: RecommendationsItemProps) => {
  const {
    productNames: domains,
    link: contentLink,
    actionType,
    message,
    image,
  } = content;

  const isLiteracyInsight =
    insightType === BOOST_EM_INSIGHT_TYPE && !!dynamicContent;
  const isLiteracyInsightWithActualData =
    isLiteracyInsight && !dynamicContent.insightButtonLink;

  const link = isLiteracyInsight
    ? dynamicContent.insightButtonLink
    : contentLink;
  const callToAction = isLiteracyInsight
    ? dynamicContent.insightButtonLabel
    : actionType;
  const text = isLiteracyInsight ? dynamicContent.headline : message;
  const thumbnailSrc = isLiteracyInsight
    ? dynamicContent.thumbnailImgLink
    : image;

  const isNew = isRecommendationNew(startRelevantDate);
  const subjectsMap = useSubjectsFromProductMap(domains);

  let callToActionBtnText = callToAction ? callToAction : 'Open';

  if (isLiteracyInsightWithActualData) {
    callToActionBtnText = 'Instructional Support';
  }

  const callToActionBtnAriaLabel = PlyrVideoUtils.isVideo(link)
    ? "View recommendation's video"
    : '';

  const [isRecommendationCardHovered, setIsRecommendationCardHovered] =
    useState<boolean>(false);

  const { showVideoModal } = useVideoModalState();

  const dispatch = useAppDispatch();

  const handleActionBtnXApiEvent = async (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    await eventService.send(XApiEventKeys.openedRecommendation, undefined, {
      result: {
        ...getRecommendationEventContextExtension(e, domains),
        recommendationId: id,
        isDynamic: isLiteracyInsight,
        recommendationSource: 'see-all',
      },
    });
  };

  const handleActionBtnClick = async (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    if (PlyrVideoUtils.isVideo(link)) {
      e.preventDefault();
      showVideoModal({
        videoSrc: link,
        posterSrc: thumbnailSrc,
      });
    }

    if (isLiteracyInsightWithActualData) {
      dispatch(setIsLiteracyInsightModalVisible(true));
      dispatch(
        setLiteracyInsightModalInfo({
          sidebar: dynamicContent.sidebar,
          recommendations: dynamicContent.recommendations,
          learnMoreLink: dynamicContent.learnMoreLink,
          createdDate,
          insightId: id,
        }),
      );
    }

    await handleActionBtnXApiEvent(e);
  };

  const handleRecommendationItemMouseEnter = () => {
    setIsRecommendationCardHovered(true);
  };

  const handleRecommendationItemMouseLeave = () => {
    setIsRecommendationCardHovered(false);
  };

  return (
    <div
      className={clsx(styles.root, 'd-flex flex-column')}
      onMouseEnter={handleRecommendationItemMouseEnter}
      onMouseLeave={handleRecommendationItemMouseLeave}
      data-testid="recommendation-item"
      style={{ height: height }}
    >
      {isNew && (
        <div className={styles.newLabelWrap}>
          <CardCornerLabel />
        </div>
      )}
      <div
        style={{ backgroundColor: `${thumbnailFallbackColor}` }}
        className={styles.thumbnailContainer}
      >
        {thumbnailSrc && (
          <img className={styles.thumbnail} src={thumbnailSrc} alt="" />
        )}
      </div>
      <div className={clsx(styles.mainContent)}>
        <RecommendationsItemDomains
          domains={domains}
          isRecommendationCardHovered={isRecommendationCardHovered}
          isStaticRecommendation={
            programAssetId === 'Static' || isLiteracyInsight
          }
          resetMaxRecommendationCardHeight={resetMaxRecommendationCardHeight}
          subjectsMap={subjectsMap}
          userPrograms={userPrograms}
        />
        {isLiteracyInsight && (
          <LiteracyInsightHeading
            isPreviewSection={false}
            header={dynamicContent.header}
          />
        )}
        <p
          className={clsx(
            'rhythm-mb-2 rhythm-pt-1 text-width scale-plus-1',
            styles.text,
          )}
        >
          {text}
        </p>
      </div>
      <div className={clsx(styles.btnWrap, 'mt-auto')}>
        <OvalButton
          href={link}
          target="_blank"
          classNames="w-100 btn-secondary text-decoration-none"
          onContextMenu={handleActionBtnXApiEvent}
          onClick={handleActionBtnClick}
          ariaLabel={callToActionBtnAriaLabel}
        >
          {callToActionBtnText}
        </OvalButton>
      </div>
    </div>
  );
};
